import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { changeOrderApi, customerApi } from '../api/endpoints.ts';
import { Alert, Button, classes, LoadingBlock, Select } from '../components/ui.tsx';
import SearchSelect from '../components/search-select.tsx';
import {
  ExtraWorkActions,
  ExtraWorkDoneNotice,
  ExtraWorkForm,
  ExtraWorkOrderState,
  type ExtraWorkDone,
} from '../components/extra-work-form.tsx';
import { useSession } from '../state/session.tsx';
import { useCommercialWork } from '../components/commercial-gate.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatLocalTime, formatShortDate } from '../i18n/datetime.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { CHANGE_ORDER_STATUSES, type ChangeOrderStatus } from '@shared/constants/index.ts';
import type { ExtraWorkVisitOption } from '@shared/types/index.ts';

/**
 * Extra work: what customers asked for beyond the usual, across customers.
 *
 * One screen rather than a section buried on the customer page, because the
 * question this answers is a daily one: "what have we added, what have we
 * asked people to approve, and who has not answered?"
 *
 * Extra work goes onto one of the customer's visits (migration 0104) — its
 * price, its invoice, its checklist — so every company that invoices can use
 * it. With no visit it rides a commercial client's monthly invoice instead,
 * which only a company doing commercial work is offered; the API refuses it
 * for anyone else, and this only renders that rule.
 */
export default function ChangeOrdersPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('changeOrders.title'));
  const queryClient = useQueryClient();
  const { can } = useSession();
  /*
    Everything, not just what is waiting on a client. "Sent" is the more
    useful daily filter and was the first instinct — but it also means an
    order you have just saved as a draft disappears the moment you save it,
    which reads as the save having failed.
  */
  const [status, setStatus] = useState<'' | ChangeOrderStatus>('');
  const [composing, setComposing] = useState(false);
  /**
   * What the last thing added, sent or agreed came to — above all the
   * approval link, which exists exactly once: only its hash is stored, so if
   * the office wants to paste it into their own email this is their only
   * chance to copy it.
   */
  const [done, setDone] = useState<ExtraWorkDone | null>(null);

  const orders = useQuery({
    queryKey: ['change-orders', status],
    queryFn: () => changeOrderApi.list(status ? { status } : {}),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['change-orders'] });

  const money = (cents: number, currencyCode: string) => formatCents(cents, currencyCode, getDateLocale());

  return (
    <WorkPage
      title={t('changeOrders.title')}
      description={t('changeOrders.description')}
      actions={
        can('quotes:write') ? (
          <Button variant="primary" onClick={() => setComposing((open) => !open)}>
            {composing ? t('common.cancel') : t('changeOrders.raise')}
          </Button>
        ) : null
      }
    >
      {done ? <ExtraWorkDoneNotice done={done} /> : null}

      {composing && can('quotes:write') ? (
        <ChangeOrderComposer
          onDone={(result) => {
            setComposing(false);
            setDone(result);
            refresh();
          }}
        />
      ) : null}

      <WorkSection title={t('changeOrders.listTitle')}>
        {/*
          This list is one page of the newest 25 with no pager under it, so on
          an account with more history than that the filter is the only way
          to reach an older draft that still needs withdrawing.
        */}
        <div className="mb-4 w-full sm:max-w-56">
          <Select
            label={t('changeOrders.filterStatus')}
            value={status}
            onChange={(event) => setStatus(event.target.value as '' | ChangeOrderStatus)}
            options={[
              { value: '', label: t('changeOrders.status.all') },
              ...CHANGE_ORDER_STATUSES.map((value) => ({ value, label: t(`changeOrders.status.${value}`) })),
            ]}
          />
        </div>

        {orders.isLoading ? <LoadingBlock label={t('changeOrders.loading')} /> : null}
        {orders.isError ? (
          <Alert tone="error" title={t('changeOrders.loadErrorTitle')}>
            {(orders.error as Error).message}
          </Alert>
        ) : null}

        {orders.data && orders.data.items.length === 0 ? (
          <WorkEmpty title={t('changeOrders.emptyTitle')}>
            <p>{t('changeOrders.emptyBody')}</p>
          </WorkEmpty>
        ) : null}

        {orders.data && orders.data.items.length > 0 ? (
          <ul className="divide-y divide-[color:var(--color-line)]">
            {orders.data.items.map((order) => (
              <li key={order.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0 basis-56 flex-1">
                  <p className="font-semibold">
                    {order.title}
                    <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                      {t(`changeOrders.status.${order.status}`)}
                    </span>
                  </p>
                  <p className="text-sm text-[color:var(--color-muted)]">
                    {order.number} · {order.customerName}
                    {order.siteName ? ` · ${order.siteName}` : ''}
                    {order.visit
                      ? ` · ${t('changeOrders.visitOn', { date: formatShortDate(order.visit.scheduledDate), time: formatLocalTime(order.visit.startTimeLocal) })}`
                      : order.serviceDate
                        ? ` · ${formatShortDate(order.serviceDate)}`
                        : ''}
                  </p>
                  <ExtraWorkOrderState order={order} />
                  {order.appointmentId && order.visit ? (
                    <Link
                      to={`/schedule?appointment=${encodeURIComponent(order.appointmentId)}`}
                      className="text-sm font-semibold text-[color:var(--color-brand)] underline"
                    >
                      {t('changeOrders.openVisit')}
                    </Link>
                  ) : null}
                  {order.declineMessage ? <p className="text-sm">{order.declineMessage}</p> : null}
                </div>
                <p className={classes('w-28 text-right font-semibold')}>
                  {money(order.amountCents, order.currencyCode)}
                </p>
                <ExtraWorkActions
                  order={order}
                  onChanged={refresh}
                  onSent={(result) =>
                    setDone({
                      order: result.changeOrder,
                      link: { url: result.approvalUrl, emailedTo: result.emailedTo },
                    })
                  }
                  onAgreed={(agreed) => setDone({ order: agreed, link: null })}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </WorkSection>
    </WorkPage>
  );
}

/**
 * Who it is for and where it goes, then the form.
 *
 * The visit list is the customer's upcoming visits, soonest first, with the
 * next one that can take extra work already chosen — "add it to their next
 * clean" is the common case. A visit that cannot take it (already paid,
 * priced from a range) is listed with the reason rather than hidden, so the
 * office is not left wondering where Tuesday went.
 */
function ChangeOrderComposer({ onDone }: { onDone: (done: ExtraWorkDone) => void }) {
  const { t } = useTranslation();
  const { company, currencyCode: sessionCurrency } = useSession();
  const commercialWork = useCommercialWork();
  const [customerId, setCustomerId] = useState('');
  const [visitChoice, setVisitChoice] = useState<string | null>(null);

  /*
    Searched, not merely paged. This picker used to be a flat first hundred,
    which is the same trap the walkthrough and invoice composers were in: a
    company past its hundredth customer could not add extra work for anybody
    whose name fell off the end, and the list looked complete while it
    happened.
  */
  const [customerSearch, setCustomerSearch] = useState('');
  const customers = useQuery({
    queryKey: ['customers', 'change-order-picker', customerSearch],
    queryFn: () => customerApi.list({ ...(customerSearch ? { q: customerSearch } : {}), pageSize: 100 }),
    placeholderData: keepPreviousData,
  });

  const visits = useQuery({
    queryKey: ['change-orders', 'visit-options', customerId],
    queryFn: () => changeOrderApi.visitOptions(customerId),
    enabled: Boolean(customerId),
  });
  const available = (visits.data?.items ?? []).filter((visit) => visit.blockedReason === null);
  // A monthly pick does not outlive commercial mode being switched off.
  const choice = visitChoice === MONTHLY && !commercialWork ? null : visitChoice;
  // The office's pick, or the next visit that can take it, or the monthly
  // invoice when there is no such visit and the company bills that way —
  // known only once the visits have loaded, so the monthly form does not
  // stand in for them while they are still on their way or failed.
  const chosen = choice ?? available[0]?.id ?? (commercialWork && visits.data ? MONTHLY : '');
  const visit = available.find((option) => option.id === chosen) ?? null;

  /*
    With no visit, extra work only bills on the consolidated monthly invoice,
    and the server refuses it for a customer billed per visit. Say it when the
    client is picked, and offer the switch right there.
  */
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<{ id: string; name: string; consolidated: boolean } | null>(null);
  const perVisit = Boolean(picked && picked.id === customerId && !picked.consolidated);
  const switchBilling = useMutation({
    mutationFn: (id: string) => customerApi.update(id, { consolidatedBilling: true }),
    onSuccess: async (updated) => {
      setPicked((current) => (current && current.id === updated.id ? { ...current, consolidated: true } : current));
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const visitLabel = (option: ExtraWorkVisitOption) => {
    const when = t('changeOrders.visitOn', {
      date: formatShortDate(option.scheduledDate),
      time: formatLocalTime(option.startTimeLocal),
    });
    const where = option.propertyAddress ? ` · ${option.propertyAddress}` : '';
    return option.blockedReason
      ? t('changeOrders.visitUnavailable', {
          visit: `${when}${where}`,
          reason: t(`changeOrders.visitBlocked.${option.blockedReason}`),
        })
      : `${when}${where}`;
  };

  return (
    <WorkSection title={t('changeOrders.raise')}>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <SearchSelect
          label={t('changeOrders.customer')}
          hint={t('searchSelect.hint')}
          placeholder={t('changeOrders.chooseCustomer')}
          value={customerId}
          onChange={(next) => {
            setCustomerId(next);
            setVisitChoice(null);
            const found = (customers.data?.items ?? []).find((customer) => customer.id === next);
            setPicked(
              found ? { id: found.id, name: found.displayName, consolidated: found.consolidatedBilling } : null,
            );
            switchBilling.reset();
          }}
          onSearch={setCustomerSearch}
          loading={customers.isFetching}
          errored={customers.isError}
          options={(customers.data?.items ?? []).map((customer) => ({
            value: customer.id,
            label: customer.displayName,
          }))}
        />
        {customerId && visits.data ? (
          <Select
            label={t('changeOrders.chooseVisit')}
            value={chosen}
            onChange={(event) => setVisitChoice(event.target.value)}
            options={[
              ...(chosen === '' ? [{ value: '', label: t('changeOrders.noVisitChosen'), disabled: true }] : []),
              ...visits.data.items.map((option) => ({
                value: option.id,
                label: visitLabel(option),
                disabled: option.blockedReason !== null,
              })),
              ...(commercialWork ? [{ value: MONTHLY, label: t('changeOrders.noVisitOption') }] : []),
            ]}
          />
        ) : null}
      </div>

      {customerId && visits.isLoading ? <LoadingBlock label={t('changeOrders.loadingVisits')} /> : null}
      {visits.isError ? (
        <Alert tone="error" title={t('changeOrders.visitsErrorTitle')}>
          {(visits.error as Error).message}
        </Alert>
      ) : null}
      {customerId && visits.data && chosen === '' ? (
        <Alert tone="info" title={t('changeOrders.noUpcomingTitle')}>
          <p>{t('changeOrders.noUpcomingBody')}</p>
        </Alert>
      ) : null}

      {chosen === MONTHLY && perVisit && picked ? (
        <Alert tone="warning" title={t('changeOrders.perVisitTitle', { name: picked.name })}>
          <p>{t('changeOrders.perVisitBody')}</p>
          {switchBilling.isError ? <p className="mt-2">{(switchBilling.error as Error).message}</p> : null}
          <Button
            className="mt-3"
            variant="secondary"
            busy={switchBilling.isPending}
            onClick={() => switchBilling.mutate(picked.id)}
          >
            {t('changeOrders.switchToMonthly')}
          </Button>
        </Alert>
      ) : null}

      {customerId && (visit || (chosen === MONTHLY && !perVisit)) ? (
        <div className="mt-2">
          <ExtraWorkForm
            // A fresh form per destination: an amount typed for a visit is
            // final with tax included, for the monthly invoice it is not.
            key={`${customerId}:${chosen}`}
            customerId={customerId}
            appointmentId={visit?.id ?? null}
            currencyCode={visit?.currencyCode ?? sessionCurrency}
            onDone={onDone}
          />
        </div>
      ) : null}
    </WorkSection>
  );
}

/** The visit picker's value for "not on a visit — the monthly invoice". */
const MONTHLY = 'monthly';
